import { createSlice } from "@reduxjs/toolkit";

import UFStoreInitialState, { ufNodeInitialState } from "./constants/UFStoreInitialState";

const getUFNode = (state, nodeId) => {
  if (!nodeId) return null;
  return state.nodes[nodeId] || null;
};

export const UFSlice = createSlice({
  name: "UFStore",
  initialState: UFStoreInitialState,
  reducers: {
    initializeUfNode: (state, action) => {
      const { nodeId } = action.payload;
      if (nodeId && !state.nodes[nodeId]) {
        state.nodes[nodeId] = { ...ufNodeInitialState };
      }
    },
    removeUfNode: (state, action) => {
      const { nodeId } = action.payload;
      if (nodeId) {
        delete state.nodes[nodeId];
      }
    },
    updateMinMaxSliderValue: (state, action) => {
      const { nodeId, name, value } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node[name] = value;
    },
    updateActiveUFModule: (state, action) => {
      const { nodeId, activeUFModule } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.activeUFModule = activeUFModule;
    },
    setIsUpdateActiveModule: (state, action) => {
      const { nodeId, isUpdateActiveModule } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isUpdateActiveModule = isUpdateActiveModule;
    },
    updateUFStore: (state, action) => {
      const { nodeId, data } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.data = data;
    },
    updateUFDefaultInputRangeConfig: (state, action) => {
      const { nodeId, defaultInputRangeConfig } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      //considered ufInputRangeConfig and ufInputRangeConfigByWaterType for all fields other than Filtrate Flux
      //considerd ufFluxGuideline for FiltrateFlux
      node.defaultInputRangeConfig = { ...node.defaultInputRangeConfig, ...defaultInputRangeConfig };
    },
    updateUFStoreData: (state, action) => {
      const { nodeId, data, calcEngineDataRefreshCount, ...rest } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      if (data) {
        node.data = { ...node.data, ...data };
      }

      if (calcEngineDataRefreshCount) {
        node.calcEngineDataRefreshCount = node.calcEngineDataRefreshCount + 1;
      }

      Object.assign(node, rest);
    },
    updateUFCommonData: (state, action) => {
      Object.assign(state, action.payload);
    },
    setIsSysDiagramVisible: (state, action) => {
      const { nodeId, isSysDesignVisible } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isSysDesignVisible = isSysDesignVisible;
    },
    updateAdditionalData: (state, action) => {
      const { nodeId, target, value } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.data[target] = value;
    },
    handleCalcEngineResponse: (state, action) => {
      const { nodeId, calcEngineData } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.calcEngineData = calcEngineData;
      node.calcEngineDataRefreshCount = -1;
      node.isRecommendationCalucalting = false;
      node.isUFConfigLoading = false;
    },
    updateUFCalcEngineDataFromReport: (state, action) => {
      const { nodeId, calcEngineData } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.calcEngineData = calcEngineData;
    },
    handleSameAsBackwash: (state, action) => {
      const { nodeId } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      const backWash1_CEB = node.defaultInputRangeConfig.backWash1_CEB;
      const backWash2_CEB = node.defaultInputRangeConfig.backWash2_CEB;

      node.data.drain = node.data.drain_backWash;
      node.data.ceb_AirScour = node.data.backwash_AirScour;
      node.data.backWash1_CEB =
        backWash1_CEB.minValue > node.data.backWash1_backWash || backWash1_CEB.maxValue < node.data.backWash1_backWash
          ? node.data.backWash1_CEB
          : node.data.backWash1_backWash;
      node.data.backWash2_CEB =
        backWash2_CEB.minValue > node.data.backWash2_backWash || backWash2_CEB.maxValue < node.data.backWash2_backWash
          ? node.data.backWash2_CEB
          : node.data.backWash2_backWash;
      node.data.forwardFlush = node.data.forwardFlush_backWash;
    },

    resetUfData: state => {
      state.nodes = {};
    },
    setCustomAvail: (state, action) => {
      const { nodeId, isCustomConfigAvail } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isCustomConfigAvail = isCustomConfigAvail;
    },
    setCustomOfflineTimePerUnit: (state, action) => {
      const { nodeId, isOfflineTimeChanged } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isOfflineTimeChanged = isOfflineTimeChanged;
    },
    setUfDataUpdate: (state, action) => {
      const { nodeId, isUfDataUpdated } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isUfDataUpdated = isUfDataUpdated;
    },
    setUFProductFilter: (state, action) => {
      const { nodeId, ...ufProductFilter } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.ufProductFilter = { ...node.ufProductFilter, ...ufProductFilter };
    },
    setIsLastFieldOnAdittionSettingScreen: (state, action) => {
      const { nodeId, isLastFieldOnAdittionSettingScreen } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.isLastFieldOnAdittionSettingScreen = isLastFieldOnAdittionSettingScreen;
    },
    updateUfChemicalScreenInitialized: (state, action) => {
      const { nodeId, screen } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.ufChemicalScreenInitialized[screen] = true;
    },
    updateUFInvalidData: (state, action) => {
      const { nodeId, ...fields } = action.payload;
      const node = getUFNode(state, nodeId);
      if (!node) return;

      node.ufInvalidData = { ...node.ufInvalidData, ...fields };
    },
  },
});

export const {
  initializeUfNode,
  removeUfNode,
  updateUFStoreData,
  updateUFCommonData,
  setIsSysDiagramVisible,
  updateUFStore,
  updateActiveUFModule,
  updateUFDefaultInputRangeConfig,
  updateAdditionalData,
  handleSameAsBackwash,
  handleCalcEngineResponse,
  resetUfData,
  setCustomAvail,
  setCustomOfflineTimePerUnit,
  setUfDataUpdate,
  updateMinMaxSliderValue,
  setUFProductFilter,
  setIsLastFieldOnAdittionSettingScreen,
  setIsUpdateActiveModule,
  updateUfChemicalScreenInitialized,
  updateUFCalcEngineDataFromReport,
  updateUFInvalidData,
} = UFSlice.actions;
export default UFSlice.reducer;
